<script setup lang="ts">
/**
 * ProseMirror editing surface for `.zwdoc` documents (#249), mounted by
 * `DocumentEditor.vue`; binds to the document text the shell saves.
 */
import {
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from "vue";
import { just, nothing, type Maybe } from "../../lib/maybe";
import type { ZwDocParseError } from "../../lib/zwdoc/format";
import type {
  EditorSelectionSnapshot,
  EditorTextRange,
  FormatCommand,
} from "../port";
import type { EditorWordRange } from "../wordRange";
import type { EditorChapterOpening, EditorImage } from "../port";
import type { EntityPeekEntry } from "../entityPeekIndex";
import type { CommentSurface } from "../commentSurface";
import type { HistoryRange } from "../../lib/proseDiff";
import {
  createProseEditor,
  type ProseEditor,
  type ProsePeekContext,
  type ProseSearchCommand,
  type ProseSearchQuery,
  type ProseShellRequest,
} from "./proseEditor";
import "prosemirror-view/style/prosemirror.css";
import "./prose.css";

const props = defineProps<{
  text: string;
  editable: boolean;
  resolveImageSrc: (src: string) => string;
  /** Outer scroller that owns overflow (`.zw-editor-scroll`). */
  scrollHost: HTMLElement | null;
  typewriter: boolean;
  /** Harper underlines (live writing only). */
  lint: boolean;
  /** Database entities to underline; [] outside scenes / live writing. */
  entityIndex: readonly EntityPeekEntry[];
  peek: ProsePeekContext;
  /** Comment highlights + Add comment (also while read-only). */
  comments: boolean;
  /** Change-history marks; [] outside history review. */
  historyRanges: readonly HistoryRange[];
  /** Initial / lead-in to mark; off unless this scene opens a chapter. */
  chapterOpening: EditorChapterOpening;
  /** Touch device: soft keyboards need autocapitalize / autocorrect. */
  mobile: boolean;
  /**
   * A keystroke landed and the serialized text is not published yet.
   * Return true when the shell will flush the draft itself.
   */
  docEdited: () => boolean;
}>();

const emit = defineEmits<{
  "update:text": [text: string];
  selectionChange: [];
  /** Link / image need shell UI (URL dialog, file picker). */
  request: [command: ProseShellRequest];
  imageClick: [image: EditorImage, element: HTMLElement];
  /** Added (or reopened) at the selection; the shell opens its thread. */
  commentAdded: [id: string];
  /** Restore from git history (or blank) when the buffer is unreadable. */
  resetDocument: [];
}>();

const host = useTemplateRef<HTMLDivElement>("host");
const parseError = ref<ZwDocParseError | null>(null);
const restoring = ref(false);
let editor: ProseEditor | null = null;

function destroyEditor(): void {
  editor?.destroy();
  editor = null;
}

function mount(): void {
  const el = host.value;
  if (el === null) return;
  destroyEditor();
  el.replaceChildren();
  const created = createProseEditor({
    host: el,
    text: props.text,
    editable: props.editable,
    resolveImageSrc: props.resolveImageSrc,
    scrollHost: () => props.scrollHost,
    onChange: (text) => emit("update:text", text),
    onDocEdited: () => props.docEdited(),
    onSelectionChange: () => emit("selectionChange"),
    onRequest: (command) => emit("request", command),
    onImageClick: (image, element) => emit("imageClick", image, element),
    lint: props.lint,
    entityIndex: props.entityIndex,
    peek: props.peek,
    comments: props.comments,
    historyRanges: props.historyRanges,
    chapterOpening: props.chapterOpening,
    mobile: props.mobile,
    onCommentAdded: (id) => emit("commentAdded", id),
  });
  if (created.ok === false) {
    parseError.value = created.error;
    return;
  }
  parseError.value = null;
  editor = created.value;
  editor.setTypewriter(props.typewriter);
}

onMounted(mount);

watch(
  () => props.text,
  async () => {
    if (editor === null) {
      // After a failed parse the host may still be settling; remount next tick.
      await nextTick();
      mount();
    } else {
      const result = editor.setText(props.text);
      // An unreadable update leaves the last good document on screen.
      parseError.value = result.ok ? null : result.error;
    }
    if (restoring.value) restoring.value = false;
  },
);

function onResetClick(): void {
  if (restoring.value) return;
  restoring.value = true;
  emit("resetDocument");
}

watch(
  () => props.editable,
  (editable) => editor?.setEditable(editable),
);

watch(
  () => props.resolveImageSrc,
  (resolve) => editor?.setImageResolver(resolve),
);

watch(
  () => props.typewriter,
  (active) => editor?.setTypewriter(active),
);

watch(
  () => props.lint,
  (enabled) => editor?.setLint(enabled),
);

watch(
  () => props.historyRanges,
  (ranges) => editor?.setHistoryRanges(ranges),
);

watch(
  () => props.comments,
  (enabled) => editor?.setCommentsEnabled(enabled),
);

watch(
  () => props.chapterOpening,
  (opening) => editor?.setChapterOpening(opening),
);
watch(
  () => props.entityIndex,
  (entries) => editor?.setEntityIndex(entries),
);

onBeforeUnmount(() => {
  editor?.publishPendingText();
  destroyEditor();
});

defineExpose({
  publishPendingText: (): void => editor?.publishPendingText(),
  focus: (): void => editor?.focus(),
  runCommand: (command: FormatCommand): boolean =>
    editor?.runCommand(command) ?? false,
  ensureCaretVisible: (behavior?: ScrollBehavior): void =>
    editor?.ensureCaretVisible(behavior),
  scrollToTextOffset: (offset: number): void =>
    editor?.scrollToTextOffset(offset),
  wordOrSelection: (): Maybe<EditorWordRange> =>
    editor?.wordOrSelection() ?? nothing,
  replaceExpectedRange: (range: EditorTextRange, text: string): boolean =>
    editor?.textPort.replaceExpectedRange(range, text) ?? false,
  selectionSnapshot: (): Maybe<EditorSelectionSnapshot> =>
    editor === null ? nothing : just(editor.selectionSnapshot()),
  collapseSelection: (): void => editor?.collapseSelection(),
  linkTarget: (): { href: string; editing: boolean } =>
    editor?.linkTarget() ?? { href: "", editing: false },
  applyLink: (href: string): boolean => editor?.applyLink(href) ?? false,
  removeLink: (): boolean => editor?.removeLink() ?? false,
  insertImage: (src: string): boolean => editor?.insertImage(src) ?? false,
  setSearch: (query: ProseSearchQuery): void => editor?.setSearch(query),
  searchCommand: (command: ProseSearchCommand): boolean =>
    editor?.searchCommand(command) ?? false,
  selectNextOccurrence: (): boolean => editor?.selectNextOccurrence() ?? false,
  selectedText: (): string => editor?.selectedText() ?? "",
  removeImage: (image: EditorImage): void => editor?.removeImage(image),
  commentSurface: (): CommentSurface | null => editor?.commentSurface ?? null,
  updateImageAlt: (image: EditorImage, alt: string): EditorImage | null =>
    editor?.updateImageAlt(image, alt) ?? null,
});
</script>

<template>
  <div class="zw-prose-root">
    <div v-if="parseError !== null" class="zw-prose-error" role="alert">
      <p>This document couldn't be opened.</p>
      <p class="zw-prose-error-detail">
        Line {{ parseError.line }}: {{ parseError.message }}
      </p>
      <p class="zw-prose-error-detail">
        The file may have been overwritten. Restore the last readable version
        from this project's git history.
      </p>
      <button
        type="button"
        class="zw-prose-error-reset"
        :disabled="restoring"
        @click="onResetClick"
      >
        {{ restoring ? "Restoring…" : "Restore last good version" }}
      </button>
    </div>
    <div ref="host" />
  </div>
</template>
